import {ZAHAV_ONLY_THRESHOLD_NIS} from '@sollapay/domain';
import {PaymentMethod, TransferType} from '@sollapay/enums';
import {
  Badge,
  Field,
  Icon,
  LabeledButtonGroup,
  RadioCard,
  RadioGroup,
  Typography
} from '@sollapay/ui/components';
import {formatNIS} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import type {PayinTransferType} from '@sollapay/enums';
import type {FC} from 'react';

const PREFIX = 'view.buyers.paymentRequest';

const PAYMENT_METHODS = [PaymentMethod.PIS, PaymentMethod.MANUAL] as const;
const TRANSFER_TYPE_OPTIONS = [TransferType.MASAV, TransferType.ZAHAV] as const;

export interface PaymentMethodCardsProps {
  method: PaymentMethod | '';
  onMethodChange: (method: PaymentMethod) => void;
  transferType: PayinTransferType;
  onTransferTypeChange: (transferType: PayinTransferType) => void;
  isPisDisabled?: boolean;
  isZahavOnly?: boolean;
}

export const PaymentMethodCards: FC<PaymentMethodCardsProps> = ({
  method,
  onMethodChange,
  transferType,
  onTransferTypeChange,
  isPisDisabled = false,
  isZahavOnly = false
}) => {
  const {t} = useTranslation('trustAccounts');

  return (
    <Field label={t(`${PREFIX}.paymentMethod`)} required>
      <RadioGroup
        value={method}
        onValueChange={value => onMethodChange(value as PaymentMethod)}
        aria-required
        className="grid grid-cols-2 gap-3"
      >
        {PAYMENT_METHODS.map(option => (
          <MethodCard
            key={option}
            method={option}
            isSelected={method === option}
            isDisabled={option === PaymentMethod.PIS && isPisDisabled}
            isZahavOnly={isZahavOnly}
            transferType={transferType}
            onTransferTypeChange={onTransferTypeChange}
          />
        ))}
      </RadioGroup>
    </Field>
  );
};

interface MethodCardProps {
  method: PaymentMethod;
  isSelected: boolean;
  isDisabled: boolean;
  isZahavOnly: boolean;
  transferType: PayinTransferType;
  onTransferTypeChange: (transferType: PayinTransferType) => void;
}

const MethodCard: FC<MethodCardProps> = ({
  method,
  isSelected,
  isDisabled,
  isZahavOnly,
  transferType,
  onTransferTypeChange
}) => {
  const {t} = useTranslation('trustAccounts');

  return (
    <RadioCard
      value={method}
      isSelected={isSelected}
      isDisabled={isDisabled}
      title={t(`${PREFIX}.methods.${method}`)}
      description={t(`${PREFIX}.methodDescriptions.${method}`)}
      titleEnd={isDisabled ? <Icon name="lock" className="size-4 text-fg-disabled" /> : undefined}
      footer={
        <MethodCardFooter
          isSelected={isSelected}
          isDisabled={isDisabled}
          isZahavOnly={isZahavOnly}
          transferType={transferType}
          onTransferTypeChange={onTransferTypeChange}
        />
      }
    />
  );
};

interface MethodCardFooterProps {
  isSelected: boolean;
  isDisabled: boolean;
  isZahavOnly: boolean;
  transferType: PayinTransferType;
  onTransferTypeChange: (transferType: PayinTransferType) => void;
}

const MethodCardFooter: FC<MethodCardFooterProps> = ({
  isSelected,
  isDisabled,
  isZahavOnly,
  transferType,
  onTransferTypeChange
}) => {
  const {t} = useTranslation('trustAccounts');

  if (isDisabled) {
    return (
      <Typography size="sm" color="tertiary" className="whitespace-normal">
        {t(`${PREFIX}.pisPrivateOnly`)}
      </Typography>
    );
  }

  if (isZahavOnly) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Typography size="sm" color="tertiary">
          {t(`${PREFIX}.transferType.label`)}:
        </Typography>
        <Typography size="sm" weight="semibold" color="secondary">
          {t(`${PREFIX}.transferType.zahavOnly`)}
        </Typography>
        <Badge variant="outline" size="sm">
          <Icon name="lock" className="size-3" />
          {t(`${PREFIX}.transferType.zahavOnlyFrom`, {
            threshold: formatNIS(ZAHAV_ONLY_THRESHOLD_NIS, {compact: true})
          })}
        </Badge>
      </div>
    );
  }

  if (isSelected) {
    return <TransferTypePicker value={transferType} onChange={onTransferTypeChange} />;
  }

  return (
    <div className="flex items-center gap-1">
      <Typography size="sm" color="tertiary">
        {t(`${PREFIX}.transferType.label`)}:
      </Typography>
      <Typography size="sm" weight="semibold" color="secondary">
        {t(`${PREFIX}.transferType.summary`)}
      </Typography>
    </div>
  );
};

interface TransferTypePickerProps {
  value: PayinTransferType;
  onChange: (transferType: PayinTransferType) => void;
}

const TransferTypePicker: FC<TransferTypePickerProps> = ({value, onChange}) => {
  const {t} = useTranslation('trustAccounts');

  const items = TRANSFER_TYPE_OPTIONS.map(type => ({
    value: type,
    label: t(`${PREFIX}.transferType.options.${type}`)
  }));

  return (
    <LabeledButtonGroup
      label={t(`${PREFIX}.transferType.label`)}
      containerClassName="gap-2 self-stretch"
      variant="segmented"
      value={value}
      onChange={next => onChange(next as PayinTransferType)}
      items={items}
    />
  );
};
